import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";

export default function Reset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      toast.success("Password reset — please sign in with your new password");
      navigate("/login");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset password");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 py-16">
        <Card className="border-2 border-gray-900 rounded-xl">
          <CardContent className="p-8" data-testid="reset-missing-token">
            <h1 className="font-heading text-2xl font-bold text-gray-900">Missing reset link</h1>
            <p className="mt-2 text-gray-700">The link is incomplete. Open the email we sent you and click the button, or start over.</p>
            <Button asChild className="mt-6 rounded-full bg-blue-600 hover:bg-blue-700">
              <Link to="/forgot">Request a new link</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Card className="border-2 border-gray-900 rounded-xl">
        <CardContent className="p-8">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-700 mb-4">
            <ArrowLeft className="w-4 h-4"/> Back to sign in
          </Link>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Choose a new password</h1>
          <p className="mt-2 text-gray-700">Your new password must be at least 6 characters.</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="rp-new" className="text-lg font-medium">New password</Label>
              <Input id="rp-new" type="password" required minLength={6} value={next} onChange={e => setNext(e.target.value)}
                className="mt-2 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="reset-new"/>
            </div>
            <div>
              <Label htmlFor="rp-confirm" className="text-lg font-medium">Confirm new password</Label>
              <Input id="rp-confirm" type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
                className="mt-2 h-12 border-2 border-gray-300 focus:border-blue-600" data-testid="reset-confirm"/>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="reset-submit">
              <KeyRound className="w-4 h-4 mr-2"/>{busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
